import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import CustomerMenu from './CustomerMenu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Package, User, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '',
    billing_name: '', billing_rfc: '',
    billing_street: '', billing_neighborhood: '',
    billing_city: '', billing_state: '', billing_postal_code: '',
  });

  useEffect(() => {
    if (authLoading || !user) return;
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('kaha_session');
        const res = await axios.get(`${API}/customer/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = res.data;
        setProfile(p);
        setForm({
          name: p.name || '',
          phone: p.phone || '',
          billing_name: p.billing?.name || '',
          billing_rfc: p.billing?.rfc || '',
          billing_street: p.billing?.street || '',
          billing_neighborhood: p.billing?.neighborhood || '',
          billing_city: p.billing?.city || '',
          billing_state: p.billing?.state || '',
          billing_postal_code: p.billing?.postal_code || '',
        });
      } catch {
        toast.error('Error al cargar perfil');
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user, authLoading]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('kaha_session');
      await axios.put(`${API}/customer/profile`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Perfil actualizado');
      refreshUser();
    } catch {
      toast.error('Error al guardar');
    }
    setSaving(false);
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <User className="w-12 h-12 text-accent mx-auto" />
            <h2 className="text-xl font-bold text-primary">Inicia sesión</h2>
            <p className="text-muted-foreground text-sm">Para ver tu perfil necesitas iniciar sesión.</p>
            <Button onClick={() => login()} className="bg-accent text-white hover:bg-accent/90 rounded-full px-6" data-testid="profile-login-btn">
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-manrope">
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <CustomerMenu />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-8">Mi perfil</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Account info */}
            <Card>
              <CardHeader className="border-b bg-muted/20">
                <CardTitle className="text-base">Información de cuenta</CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-4 mb-2">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-14 h-14 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center text-lg font-bold text-accent">
                      {user.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-primary">{user.email}</p>
                    <p className="text-xs text-muted-foreground">Google Account</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <Input value={form.name} onChange={(e) => updateField('name', e.target.value)} data-testid="profile-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Teléfono</Label>
                    <Input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+52 xxx xxx xxxx" data-testid="profile-phone" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing info */}
            <Card>
              <CardHeader className="border-b bg-muted/20">
                <CardTitle className="text-base">Datos de facturación</CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Razón social / Nombre</Label>
                    <Input value={form.billing_name} onChange={(e) => updateField('billing_name', e.target.value)} data-testid="profile-billing-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">RFC</Label>
                    <Input value={form.billing_rfc} onChange={(e) => updateField('billing_rfc', e.target.value)} data-testid="profile-billing-rfc" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Calle y numero</Label>
                  <Input value={form.billing_street} onChange={(e) => updateField('billing_street', e.target.value)} data-testid="profile-billing-street" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Colonia</Label>
                    <Input value={form.billing_neighborhood} onChange={(e) => updateField('billing_neighborhood', e.target.value)} data-testid="profile-billing-neighborhood" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ciudad</Label>
                    <Input value={form.billing_city} onChange={(e) => updateField('billing_city', e.target.value)} data-testid="profile-billing-city" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <Input value={form.billing_state} onChange={(e) => updateField('billing_state', e.target.value)} data-testid="profile-billing-state" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Código postal</Label>
                    <Input value={form.billing_postal_code} onChange={(e) => updateField('billing_postal_code', e.target.value)} data-testid="profile-billing-cp" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-accent text-white hover:bg-accent/90 rounded-full px-8 gap-2"
                data-testid="profile-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar cambios
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CustomerProfile;
